import { INSIGHT_CATEGORY, INSIGHT_TYPE } from "./account";
import { ASSET_CATEGORY } from "./assets";

export interface BaseUserEvent {
    eventId: string;
    eventType: USER_EVENT_TYPE;
    userId: string | null;
    accountId: string | null;
    clientId: string;
    contactId: number | null;
    industryId: string | null;
    payload: Record<string, unknown>;
    metadata: {
        source_service: string;
        version: string | null;
    };
    occurredAt: Date;
}

// USER ACTION EVENTS
//-------------------------------------------------------
export interface EmailSentEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.USER_ACTION;
    payload: {
        actionType: USER_ACTION_TYPE.EMAIL_SENT;
        // Email metadata
        subject: string;
        message: string;
        plannedActionId?: number | null;
        /** Stable plan action id for CRM task sync / completion matching. */
        actionId?: string | null;
        socialPromptId: number;
        numberInSeries: number | null;
        toContactId: number | null;
        /** Contact display name (person). */
        toContactName: string | null;
        /** Job title / role at the account. */
        toContactTitle: string | null;
        // Context metadata
        activityId: number | null;
        insightId: number | null;
        insightCategory: INSIGHT_CATEGORY | null;
        markedManually: boolean;
    };
}

export interface CallMadeEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.USER_ACTION;
    payload: {
        actionType: USER_ACTION_TYPE.CALL_MADE;
        // Call metadata
        callScript: string;
        plannedActionId?: number | null;
        /** Stable plan action id for CRM task sync / completion matching. */
        actionId?: string | null;
        socialPromptId: number;
        numberInSeries: number | null;
        toContactId: number | null;
        toContactName: string | null;
        toContactTitle: string | null;
        outcome: CALL_OUTCOME | null;
        responseSummary: string | null;
        sentiment: SENTIMENT | null;

        // Context metadata
        activityId: number | null;
        insightId: number | null;
        insightCategory: INSIGHT_CATEGORY | null;
        markedManually: boolean;
    };
}

export interface LinkedinEngagementEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.USER_ACTION;
    payload: {
        actionType: USER_ACTION_TYPE.LINKEDIN_ENGAGEMENT;
        // LinkedIn engagement metadata
        messageType: LINKEDIN_ENGAGEMENT_TYPE;
        engagementMessage: string | null;
        post_url: string | null;
        plannedActionId?: number | null;
        actionId?: string | null;
        socialPromptId: number | null;
        numberInSeries: number | null;
        toContactId: number | null;
        toContactName: string | null;
        toContactTitle: string | null;
        // Context metadata
        activityId: number | null;
        insightId: number | null;
        insightCategory: INSIGHT_CATEGORY | null;
        markedManually: boolean;
    };
}

export interface BlogPublishedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.USER_ACTION;
    payload: {
        actionType: USER_ACTION_TYPE.BLOG_PUBLISHED;
        engagementMessage: string | null;
        plannedActionId?: number | null;
        actionId?: string | null;
        socialPromptId: number | null;
        numberInSeries: number | null;
        toContactId: number | null;
        toContactName: string | null;
        toContactTitle: string | null;
        activityId: number | null;
        insightId: number | null;
        insightCategory: INSIGHT_CATEGORY | null;
        markedManually: boolean;
    };
}

// RESPONSE RECEIVED EVENTS
//-------------------------------------------------------
export interface EmailResponseReceivedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.RESPONSE_RECEIVED;
    payload: {
        responseType: RESPONSE_TYPE.EMAIL_REPLIED;
        // Email Response metadata
        sentiment: SENTIMENT | null;
        socialPromptId: number;
        responseSummary: string | null;

        // Context metadata
        activityId: number | null;
        insightId: number | null;
        insightCategory: INSIGHT_CATEGORY | null;
        markedManually: boolean;
    };
}

export interface LinkedinEngagementResponseReceivedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.RESPONSE_RECEIVED;
    payload: {
        // LinkedIn engagement response metadata
        responseType: RESPONSE_TYPE.LINKEDIN_DM_REPLIED | RESPONSE_TYPE.LINKEDIN_CONNECTION_REQUEST_REPLIED;
        sentiment: SENTIMENT | null;
        socialPromptId: number | null;
        responseSummary: string | null;
        connectionOutcome: LINKEDIN_CONNECTION_OUTCOME | null;

        // Context metadata
        activityId: number | null;
        insightId: number | null;
        insightCategory: INSIGHT_CATEGORY | null;
        markedManually: boolean;
    };
}


// INSIGHT COMPLETED EVENTS
//-------------------------------------------------------
export interface InsightCompletedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.INSIGHT_COMPLETED;
    payload: {
        insightId: number;
        insightCategory: INSIGHT_CATEGORY | null;
    };
}

// INSIGHT DELETED EVENTS
//-------------------------------------------------------
export interface InsightDismissedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.INSIGHT_DISMISSED;
    payload: {
        insightId: number;
        insightCategory: INSIGHT_CATEGORY | null;
    };
}

// CONTACT COMPLETED EVENTS
//-------------------------------------------------------
export interface ContactCompletedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.CONTACT_COMPLETED;
    payload: {
        contactId: number;
    };
}

// CONTACT DELETED EVENTS
//-------------------------------------------------------
export interface ContactDismissedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.CONTACT_DISMISSED;
    payload: {
        contactId: number;
    };
}

// ACTION DISMISSED EVENTS
//-------------------------------------------------------
export interface DismissedPromptSnapshot {
    promptId: number | null;
    subject: string | null;
    message: string;
    format: "text" | "markdown";
    contentType: string | null;
}

export interface ActionDismissedEvent extends BaseUserEvent {
    eventType: USER_EVENT_TYPE.ACTION_DISMISSED;
    payload: {
        actionType: string;
        actionId?: string | null;
        sourceInsightId: number | null;
        promptId: number | null;
        contactId: number | null;
        channel: string | null;
        dismissalReason?: string | null;
        dismissedPromptSnapshot?: DismissedPromptSnapshot | null;
    };
}

// ENUMS
//-------------------------------------------------------
export enum LINKEDIN_ENGAGEMENT_TYPE {
    ENGAGED = "ENGAGED",
    COMMENT = "COMMENT",
    LIKE = "LIKE",
    POST = "POST",
    DM = "DM",
    CONNECTION_REQUEST = "CONNECTION_REQUEST",
}

export enum USER_EVENT_TYPE {
    USER_ACTION = "USER_ACTION",
    RESPONSE_RECEIVED = "RESPONSE_RECEIVED",
    INSIGHT_CREATED = "INSIGHT_CREATED",
    CONTACT_ADDED = "CONTACT_ADDED",
    CONTACT_ENRICHED = "CONTACT_ENRICHED",
    SOCIAL_PROMPT_CREATED = "SOCIAL_PROMPT_CREATED",
    ACCOUNT_UPDATED = "ACCOUNT_UPDATED",
    BUYING_SIGNAL_DETECTED = "BUYING_SIGNAL_DETECTED",
    INSIGHT_COMPLETED = "INSIGHT_COMPLETED",
    INSIGHT_DISMISSED = "INSIGHT_DISMISSED",
    CONTACT_COMPLETED = "CONTACT_COMPLETED",
    CONTACT_DISMISSED = "CONTACT_DISMISSED",
    ACTION_DISMISSED = "ACTION_DISMISSED",
}

export enum USER_ACTION_TYPE {
    EMAIL_SENT = "EMAIL_SENT",
    CALL_MADE = "CALL_MADE",
    LINKEDIN_ENGAGEMENT = "LINKEDIN_ENGAGEMENT",
    BLOG_PUBLISHED = "BLOG_PUBLISHED",
}

export enum RESPONSE_TYPE {
    EMAIL_REPLIED = "EMAIL_REPLIED",
    LINKEDIN_DM_REPLIED = "LINKEDIN_DM_REPLIED",
    LINKEDIN_CONNECTION_REQUEST_REPLIED = "LINKEDIN_CONNECTION_REQUEST_REPLIED",
}

export enum SENTIMENT {
    POSITIVE = "POSITIVE",
    NEGATIVE = "NEGATIVE",
    NEUTRAL = "NEUTRAL",
}

export enum CALL_OUTCOME {
    ANSWERED = "ANSWERED",
    NOT_ANSWERED = "NOT_ANSWERED",
    VOICEMAIL = "VOICEMAIL",
    BUSY = "BUSY",
}

export enum LINKEDIN_CONNECTION_OUTCOME {
    ACCEPTED = "ACCEPTED",
    REJECTED = "REJECTED",
}

export enum SOCIAL_PROMPT_CONTENT_TYPE {
    EMAIL = "EMAIL",
    CALL = "CALL",
    LINKEDIN_POST = "LINKEDIN_POST",
    LINKEDIN_DM = "LINKEDIN_DM",
    BLOG = "BLOG",
}

export type UserEvent =
    | EmailSentEvent
    | CallMadeEvent
    | LinkedinEngagementEvent
    | BlogPublishedEvent
    | EmailResponseReceivedEvent
    | LinkedinEngagementResponseReceivedEvent
    | InsightCompletedEvent
    | InsightDismissedEvent
    | ContactCompletedEvent
    | ContactDismissedEvent
    | ActionDismissedEvent;
